import { UI, Stack } from '@genial/components';
import { BoxRadius, Spacing, Typography } from '@genial/design-tokens';
import { Box, Pressable } from 'native-base';
import React from 'react';

import { InputMoney } from '../../../../../../../../../components/InputMoney';
import { RiskRating } from '../../../../../../../../../components/RiskRating';
import {
  TableContent,
  TableContentItemColumn,
  TableContentItemText,
} from '../../../../../../../../../components/TableContent';
import { useGlobalAssetContext } from '../../../../../contexts';
import { useDirectTreasuryAssetContext } from '../../../../../contexts/products/DirectTreasury';
import { useDirectTreasuryAssetController } from '../../../../../hooks/products/DirectTreasury';
import { LateralBar } from '../../../components/LateralBar';
import { directTreasuryConstants } from '../constants';
import { ListLoadingItemDesktop } from '../ListLoadingItem/desktop';

import { DirectTreasuryListItemTypes } from '.';

export const DirectTreasuryListItemDesktop: React.FC<DirectTreasuryListItemTypes.Props> = ({
  offer,
}) => {
  const id = offer.productId;
  const {
    control,
    amountItem,
    currentValue,
    currentValueFormatted,
    handleChangeAmountItem,
    handleInputBlur,
    handleInputClear,
    handleInputFocus,
    handleSubmit,
    hasError,
    helpText,
    helpTextProps,
    isFocused,
    showMessageAdjustUnitPrice,
    productsDetailText,
  } = useDirectTreasuryAssetController(offer);
  const { setModalOpen, setOfferSelected } = useDirectTreasuryAssetContext();
  const { infoGetter, selectedTab } = useGlobalAssetContext();

  const completeOffer = { ...offer, orderQuantity: amountItem || 0, orderValue: currentValue || 0 };

  const { Button, isSelected, forceLoading } = infoGetter(
    'directTreasury',
    completeOffer,
    'productId'
  );

  const onOpenDetailsModal = () => {
    setModalOpen(true);
    setOfferSelected(offer);
  };

  const getFlex = (index: number) => {
    const currentHead = directTreasuryConstants.tableHead.find((_, i) => i === index);
    return currentHead?.flex || 1;
  };

  if (forceLoading) {
    return <ListLoadingItemDesktop />;
  }

  return (
    <TableContent
      direction="row"
      space={0}
      p={0}
      px={Spacing['inline.xxxs']}
      h="80px"
      isHighlighted={isSelected}>
      <LateralBar
        backgroundColor={selectedTab.color}
        borderTopLeftRadius={BoxRadius.sm}
        borderBottomLeftRadius={BoxRadius.sm}
      />
      <TableContentItemColumn flex={getFlex(0)}>
        <UI.Tooltip testID="details-tooltip" label={productsDetailText}>
          <Pressable onPress={onOpenDetailsModal}>
            <UI.Icon name="Info" size="xs" />
          </Pressable>
        </UI.Tooltip>
      </TableContentItemColumn>
      <TableContentItemColumn flex={getFlex(1)}>
        <TableContentItemText>{offer.productNameNormalized}</TableContentItemText>
      </TableContentItemColumn>
      <TableContentItemColumn flex={getFlex(2)}>
        <TableContentItemText>{offer.profitabilityNormalized}</TableContentItemText>
      </TableContentItemColumn>
      <TableContentItemColumn flex={getFlex(3)}>
        <RiskRating
          currentRiskRating={Number(offer.riskRatingLevelNormalized) || 0}
          tooltip={offer.riskRatingNormalized}
        />
      </TableContentItemColumn>
      <TableContentItemColumn flex={getFlex(4)}>
        <TableContentItemText>{offer.maturityDateFormattedNormalized}</TableContentItemText>
      </TableContentItemColumn>
      <TableContentItemColumn flex={getFlex(5)}>
        <TableContentItemText>{offer.feesPaymentNormalized}</TableContentItemText>
      </TableContentItemColumn>
      <TableContentItemColumn flex={getFlex(6)}>
        <Stack pr={Spacing['inline.xxxs']}>
          <UI.InputStepper
            stepperValue={0.01}
            max={1000000}
            min={offer.minimumStepQuantity || 0}
            name="treasury-amount-order-input"
            testId="treasury-amount-order-input"
            keyboardType="numeric"
            fontSize={Typography['fontsize.md']}
            defaultValue={amountItem || 0}
            onChange={handleChangeAmountItem}
            showErrorMessage={false}
            disabled={isSelected}
          />
        </Stack>
      </TableContentItemColumn>
      <TableContentItemColumn flex={getFlex(7)}>
        <Stack pr={Spacing['inline.xxxs']}>
          <InputMoney
            id={`directTreasury-input${id}`}
            name="priceItem"
            control={control}
            handleFocus={handleInputFocus}
            handleBlur={handleInputBlur}
            handleSubmit={handleSubmit}
            handleClear={handleInputClear}
            isDisabled={isSelected}
            currentValue={currentValueFormatted}
            hasFocus={isFocused}
            minValue={offer.minimumApplication || 0}
            customInputProps={{
              ...{
                ...(showMessageAdjustUnitPrice && {
                  helpTextProps,
                  helpText,
                }),
              },
              ...{ ...(isFocused && hasError && { error: hasError }) },
              ...{ ...(isFocused && !hasError && { helpText }) },
            }}
          />
        </Stack>
      </TableContentItemColumn>
      <TableContentItemColumn flex={getFlex(8)}>
        <Box w={'100%'}>
          <Button
            id={selectedTab.typeGetterId}
            offer={completeOffer}
            productContextConstant={selectedTab}
          />
        </Box>
      </TableContentItemColumn>
    </TableContent>
  );
};
